import React, { useState, useEffect } from 'react';
import { X, Send, FileText, MapPin, Box } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import api from './axiosConfig';
import toast from 'react-hot-toast';

const CreateWorkOrderModal = ({ isOpen, onClose, initialData = {} }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState({
    description: '',
    assetnum: initialData.assetnum || null,
    location_id: initialData.location_id || null,
    site_id: initialData.site_id || 'BEDFORD',
    org_id: initialData.org_id || 'EAGLE'
  });

  useEffect(() => {
    if (isOpen) {
      setFormData(prev => ({
        ...prev,
        assetnum: initialData.assetnum || null,
        location_id: initialData.location_id || null,
        site_id: initialData.site_id || 'BEDFORD',
        org_id: initialData.org_id || 'EAGLE'
      }));
    }
  }, [isOpen, initialData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const numRes = await api.get(`/wo/next-number/${formData.org_id}`);
      const wonum = numRes.data.next_wonum;

      const payload = {
        wonum,
        description: formData.description,
        status: 'WAPPR',
        wo_type: 'CM',
        assetnum: formData.assetnum || null,
        location_id: formData.location_id || null,
        site_id: formData.site_id,
        org_id: formData.org_id
      };

      await api.post('/workorder/save', payload);
      toast.success(t('createWOModal.successMsg', { wonum }));
      onClose();
    } catch (err) {
      console.error(err);
      toast.error(t('createWOModal.failMsg'));
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="bg-white w-[500px] rounded-lg shadow-2xl overflow-hidden animate-in zoom-in duration-200 border border-white/20">
        <div className="bg-[#005495] text-white p-4 flex justify-between items-center">
          <h2 className="font-black flex items-center gap-2 uppercase tracking-tighter text-sm">
            <FileText size={18} className="text-orange-400" /> {t('createWOModal.createTitle')}
          </h2>
          <button onClick={onClose} className="hover:bg-white/20 p-1 rounded-full transition-colors"><X size={20}/></button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="flex flex-col gap-1">
            <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">{t('createWOModal.summaryDescription')}</label>
            <input
              required
              placeholder={t('createWOModal.summaryPlaceholder')}
              className="border-b-2 border-gray-100 focus:border-blue-600 outline-none py-2 font-bold text-gray-700 transition-all"
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
            />
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest text-right">{t('createWOModal.contextInfo')}</label>
            <div className="flex justify-end items-center gap-2 mt-1">
              {initialData.assetnum ? (
                <div className="flex items-center gap-1 bg-blue-50 text-blue-700 px-2 py-1 rounded border border-blue-100 font-black text-[10px]">
                  <Box size={12}/> {initialData.assetnum}
                </div>
              ) : initialData.location_id ? (
                <div className="flex items-center gap-1 bg-orange-50 text-orange-700 px-2 py-1 rounded border border-orange-100 font-black text-[10px]">
                  <MapPin size={12}/> {initialData.location_id}
                </div>
              ) : (
                <span className="text-gray-400 italic text-[10px]">{t('createWOModal.generalWO')}</span>
              )}
            </div>
          </div>

          <div className="pt-4 flex gap-2">
            <button
              type="submit"
              disabled={loading}
              className="flex-1 bg-[#005495] text-white py-2.5 rounded font-black hover:bg-[#1a3353] transition-all flex items-center justify-center gap-2 shadow-lg shadow-blue-900/20 disabled:opacity-50"
            >
              {loading ? <div className="animate-spin h-4 w-4 border-2 border-white/30 border-t-white rounded-full"/> : <Send size={16}/>}
              {t('createWOModal.submitButton')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateWorkOrderModal;
